import { PublicClientApplication } from "@azure/msal-browser";

/* ═══════════════════════════════════════════════════════════════
   MSAL Configuration — Entra ID (Azure AD) single-tenant app
   ═══════════════════════════════════════════════════════════════

   Set these in your .env (or .env.local) file:
     REACT_APP_MSAL_CLIENT_ID       — App registration client ID from Entra ID
     REACT_APP_MSAL_AUTHORITY        — e.g. https://login.microsoftonline.com/{your-tenant-id}
     REACT_APP_MSAL_REDIRECT_URI     — e.g. http://localhost:3000 (defaults to window.location.origin)

   When REACT_APP_MSAL_CLIENT_ID is not set (or is the placeholder),
   auth is skipped entirely so local dev works without Entra config.
   ═══════════════════════════════════════════════════════════════ */

const PLACEHOLDER_CLIENT_ID = "YOUR_ENTRA_APP_CLIENT_ID";

const clientId =
  process.env.REACT_APP_MSAL_CLIENT_ID || PLACEHOLDER_CLIENT_ID;

const authority =
  process.env.REACT_APP_MSAL_AUTHORITY ||
  "https://login.microsoftonline.com/YOUR_TENANT_ID"; // Replace with your Entra tenant ID

const redirectUri =
  process.env.REACT_APP_MSAL_REDIRECT_URI ||
  (typeof window !== "undefined" ? window.location.origin : "http://localhost:3000");

/**
 * Returns true when MSAL is properly configured (not using placeholder values).
 * When false, the app skips authentication and renders the portal directly.
 */
export const isMsalConfigured =
  clientId !== PLACEHOLDER_CLIENT_ID && !clientId.startsWith("YOUR_");

/**
 * MSAL configuration object.
 * @see https://learn.microsoft.com/en-us/entra/identity-platform/msal-js-initializing-client-applications
 */
export const msalConfig = {
  auth: {
    clientId,
    authority,
    redirectUri,
    postLogoutRedirectUri: redirectUri,
    navigateToLoginRequestUrl: true,
  },
  cache: {
    cacheLocation: "sessionStorage",
    storeAuthStateInCookie: false,
  },
};

/**
 * Custom API scope for our backend app registration. When present, it is
 * requested at login time and used to acquire bearer tokens for the API.
 */
const apiScope = process.env.REACT_APP_API_SCOPE;

/**
 * Scopes requested during login.
 * - openid, profile, email: standard OIDC claims
 * - User.Read: read the signed-in user's profile from Microsoft Graph
 * - apiScope (optional): consent to call our backend API
 */
export const loginRequest = {
  scopes: ["openid", "profile", "email", "User.Read", ...(apiScope ? [apiScope] : [])],
};

/**
 * Scopes used by acquireTokenSilent / acquireTokenPopup when fetching the
 * access token to attach as `Authorization: Bearer …` on API calls.
 */
export const apiTokenRequest = {
  scopes: apiScope ? [apiScope] : [],
};

/**
 * Singleton MSAL PublicClientApplication instance.
 * Only initialise when MSAL is properly configured to avoid console errors.
 */
export const msalInstance = isMsalConfigured
  ? new PublicClientApplication(msalConfig)
  : null;
